import { mapBaseURL } from "@/constants";
import { useEffect, useState } from "react";

// TypeScript interfaces for Marine Ports GeoJSON response
export interface PortProperties {
  OBJECTID: number;
  ID: number;
  PORT_NAME: string;
  IMPORTS: number;
  EXPORTS: number;
  DOMESTIC: number;
  SE_ANNO_CAD_DATA: string;
  PORT: number;
  TYPE: string;
  LATITUDE1: number;
  LONGITUDE1: number;
  RANK: number;
  TOTAL: number;
  FOREIGN: number;
}

export interface PortGeometry {
  type: "Point";
  coordinates: [number, number]; // [longitude, latitude]
}

export interface PortFeature {
  type: "Feature";
  properties: PortProperties;
  geometry: PortGeometry;
}

export interface MarinePortsResponse {
  type: "FeatureCollection";
  name: string;
  features: PortFeature[];
}

// Custom fetch-based hook to replace RTK Query
export const useFetchMarinePortsQuery = () => {
  const [data, setData] = useState<MarinePortsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchMarinePorts = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        const response = await fetch(
          `${mapBaseURL}GeoJson/Principal_Ports.geojson`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const marinePortsData: MarinePortsResponse = await response.json();

        if (!isCancelled) {
          setData(marinePortsData);
        }
      } catch (err) {
        if (!isCancelled) {
          setIsError(true);
          setError(
            err instanceof Error ? err : new Error("Unknown error occurred"),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchMarinePorts();

    return () => {
      isCancelled = true;
    };
  }, []);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};
